import { getQueryParams, openUrl } from '@btcdirect/functions';
import { useApiCommunicator } from '@btcdirect/shared/src/composables/api/communicator';
import { Endpoints } from '@btcdirect/shared/src/composables/api/endpoints';
import { InitializationStates } from '@btcdirect/shared/src/composables/api/types';
import { useUserLogin } from '@btcdirect/shared/src/composables/api/user/login';
import { useLocale } from '@btcdirect/shared/src/composables/util/locale';
import { useLogger } from '@btcdirect/shared/src/composables/util/logger';
import { useClientInfoStore } from '@btcdirect/stores';
import { DeepReadonly, computed, reactive, readonly } from 'vue';

interface State {
    info: SystemInfo;
    initialized: InitializationStates;
}

interface SystemInfo {
    urls: PublicUrl[];
    supportedRegions?: string[];
    privateUrls?: PrivateUrl[];
    nationalities?: Country[];
}

type PublicUrlType = 'faq' | 'forgotPassword' | 'privacyPolicy' | 'register' | 'support' | 'termsAndConditions';
type PublicUrl = {
    [key in PublicUrlType]: {
        [locale: string]: string;
    };
};

type PrivateUrlType = 'dashboard' | 'dca' | 'kyc' | 'transactions' | 'verifyBankAccount';
type PrivateUrl = {
    [key in PrivateUrlType]: {
        [locale: string]: string;
    };
};

export interface Country {
    name: string;
    code: string;
    idSelfieRequired?: boolean;
}

const { debug, warn } = useLogger();

const state = reactive<State>({
    info: {
        urls: [],
        privateUrls: [],
        nationalities: [],
        supportedRegions: []
    },
    initialized: InitializationStates.NOT_INITIALIZED
});

export const useSystemInfo = () => {
    const { locale, fallback, setLocale } = useLocale();

    const getSystemInfo = async (): Promise<DeepReadonly<SystemInfo>> => {
        if (InitializationStates.INITIALIZED === state.initialized && !!state.info.urls) {
            return Promise.resolve(readonly(state.info));
        }

        try {
            const systemInfo = await retrieveSystemInfo();

            // Re-set the locale as we can now check if the locale is an allowed value
            setLocale(locale.value || fallback.value);

            return readonly(systemInfo);
        } catch (exception) {
            return Promise.reject(exception);
        }
    };

    const getPublicLink = (key: PublicUrlType): string => {
        if (InitializationStates.INITIALIZED !== state.initialized) {
            return null;
        }

        if (!state.info.urls[key]) {
            warn(`Could not retrieve system url with key ${key}`);
            return null;
        }

        return state.info.urls[key][locale.value] || state.info.urls[key][fallback.value];
    };

    const getPrivateLink = (key: PrivateUrlType): string => {
        if (InitializationStates.INITIALIZED !== state.initialized) {
            return null;
        }

        if (!state.info.privateUrls[key]) {
            warn(`Could not retrieve system url with key ${key}`);
            return null;
        }

        return state.info.privateUrls[key][locale.value] || state.info.privateUrls[key][fallback.value];
    };

    /**
     * Translate countries using Intl.DisplayNames
     * If locale is set then it has priority over the fallback locale.
     */
    const translateCountries = (countries: Country[]): Country[] => {
        const locales = [fallback.value];
        if (locale.value) {
            locales.unshift(locale.value);
        }

        try {
            const regionNames = new Intl.DisplayNames(locales, { type: 'region' });
            countries.forEach((country) => (country.name = regionNames.of(country.code)));
        } catch (exception) {
            warn('Could not translate country names', `Either locales "${locales.join(', ')}" were incorrect or the browser is not able to translate them.`);
        } finally {
            // If the try-part failed, this contains the English names as a fallback (BTC-2434)
            return countries.sort((a, b) => a.name.localeCompare(b.name));
        }
    };

    const clearSystemInfo = () => {
        state.info = {
            urls: [],
            nationalities: []
        };
        state.initialized = InitializationStates.NOT_INITIALIZED;
    };

    const redirectToMyBTC = async (href: string, windowTarget: 'blank' | 'self' = 'blank'): Promise<void> => {
        const clientInfoStore = useClientInfoStore();
        const { getLoginToken } = useUserLogin();

        const loginToken = await getLoginToken();

        const params = getQueryParams(href);
        params.set('token', loginToken.token);
        params.set('uuid', loginToken.uuid);

        const [url] = href.split('?');
        openUrl(`${url}?${params.toString()}`, !!clientInfoStore.client.myBtcRedirectsNewTab ? 'blank' : windowTarget);
    };

    if (InitializationStates.NOT_INITIALIZED === state.initialized) {
        getSystemInfo()
            .then(() => {
                debug('Successfully retrieved system info');
            })
            .catch(() => {
                warn('Could not retrieve system info');
            });
    }

    return {
        nationalities: computed<Country[]>(() => translateCountries(state.info.nationalities)),
        supportedRegions: computed<string[]>(() => state.info.supportedRegions),
        // TODO: Add the plain MyBTC link to the system info API response
        mybtcUrl: computed<string>(() => getPublicLink('register')?.match(/^(http[s]?:\/\/?.*?\/[a-zA-Z]{2}-[a-zA-Z]{2}\/)/)[0]),
        getPrivateLink,
        getPublicLink,
        getSystemInfo,
        translateCountries,
        clearSystemInfo,
        redirectToMyBTC
    };
};

/**
 * Get the system info from the API.
 */
async function retrieveSystemInfo(): Promise<SystemInfo> {
    const { get } = useApiCommunicator(Endpoints.SYSTEM_INFO);

    state.initialized = InitializationStates.INITIALIZING;

    try {
        state.info = (await get()) as SystemInfo;
        return state.info;
    } catch (exception) {
        throw exception;
    } finally {
        state.initialized = InitializationStates.INITIALIZED;
    }
}
